import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, useAuth } from "../App";
import { toast } from "sonner";
import UserAutocomplete from "../components/UserAutocomplete";

export default function ProfileSetupPage() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    full_name: user?.full_name || user?.name || "",
    designation: user?.designation || "",
  });
  const [managerId, setManagerId] = useState(user?.reports_to || "");
  const [noManager, setNoManager] = useState(false);
  const [saving, setSaving] = useState(false);

  // If the profile is already complete (flag or a saved designation), don't
  // strand a returning user on the setup form — bounce them straight in.
  useEffect(() => {
    if (user && (user.profile_completed || (user.designation && (user.full_name || user.name) && user.reports_to))) {
      navigate("/me", { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.designation.trim()) {
      toast.error("Name and designation are required");
      return;
    }
    if (!managerId && !noManager) {
      toast.error("Pick your manager — or tick “I don’t report to anyone”");
      return;
    }
    setSaving(true);
    try {
      await api.post("/users/profile", { ...form, manager_id: noManager ? "" : managerId });
      const res = await api.get("/auth/me");
      updateUser(res.data);
      toast.success("Profile completed!");
      navigate("/me");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--s1)] flex items-center justify-center px-6" data-testid="profile-setup">
      <div className="glass-card rounded-2xl p-8 max-w-md w-full">
        <h1 className="font-heading text-2xl text-[var(--t1)] mb-2">COMPLETE YOUR PROFILE</h1>
        <p className="text-[var(--t3)] text-sm mb-6 normal-case font-normal">
          We need your name, designation and manager before you can access Dream Machine.
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-[var(--t3)] text-xs mb-1 block">Full Name *</label>
            <input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              placeholder="e.g., Dhruv Abrol" className="form-input" required data-testid="setup-name" />
          </div>
          <div>
            <label className="text-[var(--t3)] text-xs mb-1 block">Designation *</label>
            <input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })}
              placeholder="e.g., Creative Director, Executive Producer"
              className="form-input" required data-testid="setup-designation" />
            <p className="text-[var(--t3)] text-[11px] mt-1 normal-case font-normal">Enter it exactly as it appears on your Dentsu offer letter.</p>
          </div>
          <div>
            <label className="text-[var(--t3)] text-xs mb-1 block">Manager {noManager ? "" : "*"}</label>
            {!noManager && (
              <UserAutocomplete value={managerId} onChange={setManagerId}
                placeholder="Type @ or your manager's name" />
            )}
            <label className="flex items-center gap-2 text-[var(--t3)] text-[12px] mt-2 normal-case font-normal cursor-pointer">
              <input type="checkbox" checked={noManager} onChange={(e) => { setNoManager(e.target.checked); if (e.target.checked) setManagerId(""); }} />
              I don’t report to anyone (top of the org)
            </label>
          </div>
          <button type="submit" disabled={saving} data-testid="setup-submit"
            className="w-full py-3 bg-[var(--r)] text-white font-bold rounded-xl hover:bg-[var(--r)] transition-colors disabled:opacity-50">
            {saving ? "Saving..." : "Continue to Dream Machine"}
          </button>
        </form>
      </div>
    </div>
  );
}
